"use client";

import { useState } from "react";
import { Button } from "@tranquility-ai/theme-components/button";
import { cn } from "@/lib/utils";

/**
 * In-viewport disclaimer shown only in local development when
 * `@tranquility-ai/theme-components` is resolved from a symlink (via `npm link`
 * or a manual `node_modules` symlink) instead of the published registry package.
 *
 * This makes it obvious that components like the Combobox on screen reflect
 * unpublished local edits, not the version other environments will ship. The
 * flag is computed at build time in next.config.mjs (see
 * NEXT_PUBLIC_THEME_COMPONENTS_LOCAL); it is an empty string in any environment
 * using the published package, so this component renders nothing there.
 */
export function LocalPackageBanner() {
  const localPath = process.env.NEXT_PUBLIC_THEME_COMPONENTS_LOCAL;
  const [dismissed, setDismissed] = useState(false);

  if (!localPath || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[100050] flex items-center justify-center gap-3",
        "border-t border-amber-300/60 bg-amber-400 px-4 py-2 text-sm font-medium text-amber-950",
        "shadow-[0_-4px_12px_rgba(0,0,0,0.15)]",
      )}
    >
      <span aria-hidden className="text-base leading-none">
        &#9888;
      </span>
      <span className="text-center">
        Local dev: using the{" "}
        <span className="font-semibold">symlinked</span>{" "}
        <code className="rounded bg-amber-950/10 px-1 py-0.5 font-mono text-[0.8em]">
          @tranquility-ai/theme-components
        </code>{" "}
        instead of the published version.
      </span>
      <Button
        type="button"
        size="sm"
        style="text"
        color="dark"
        background="default"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss local package notice"
        className={cn(
          "ml-1 shrink-0 rounded px-2 py-0.5 text-amber-950/80",
          "hover:bg-amber-950/10 hover:text-amber-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-950/40",
        )}
      >
        Dismiss
      </Button>
    </div>
  );
}

export default LocalPackageBanner;
